/**
 * The app's icon set - the drawings themselves, as inline SVG bodies.
 *
 * ## Why this file exists at all
 *
 * The app drew its icons as emoji for 144 rounds (📊 🎬 ✨ 🤖 ✋ 📒 …), and an emoji is not a
 * picture - it is a REQUEST for a picture, answered differently by every OS. Apple, Windows, Android
 * and every Linux font pack ship their own art, so the same button was a different image on every
 * machine the app ran on, at a weight and color nothing here could set. That is the whole of the
 * problem: not that emoji are ugly, but that the app had no controlled visual identity and no way to
 * make an icon match the theme it sits in. An SVG the app owns is one drawing everywhere, in
 * `currentColor`, at whatever size the call site asks for.
 *
 * ## Why it is INLINED per icon, and not a document sprite
 *
 * The obvious shape - one hidden `<svg>` of `<symbol>`s in `index.html`, referenced as
 * `<use href="#icon-target">` - cannot work in this app, and the reason is structural rather than a
 * matter of taste: **an SVG fragment reference is TREE-SCOPED**, the same family as `@keyframes`
 * (round 99) and the `*{box-sizing}` reset (round 92), and every island and component in this app
 * has been shadow DOM since round 100. Measured in Chrome rather than assumed, because it is the
 * assumption everything else here rests on: a `<use href="#star">` pointing at a document-level
 * sprite renders a **0×0** box inside a shadow root, against 20×18 for the identical markup in light
 * DOM and 20×18 for a sprite duplicated INSIDE that same shadow root. Nothing throws, nothing logs -
 * the icon is simply not there, which is the worst possible failure mode for a change that touches
 * every screen at once.
 *
 * So each icon carries its own path data, `<gh-icon>` inlines it, and the sprite question never
 * arises. The cost is real and worth naming: the same 30-byte path ships once per rendered instance
 * rather than once per document. It is paid in the APP bundle, which is warm, on the side of the
 * boundary where round 63 already decided a framework earns its keep - and it is exactly the trade
 * the published pages refuse (see the root CLAUDE.md: nothing in this file may ever cross into
 * `linkPage.ts`/`bioPage.ts`/`routes/epk.ts`, which are measured in bytes to first play).
 *
 * ## Two visual voices, on purpose
 *
 * Most entries are **stroke** icons on a 24×24 grid: `fill="none"`, `stroke="currentColor"`, 1.75
 * units wide, round caps and joins. One geometry, one weight, one optical size - that consistency IS
 * the identity, and it is what an emoji set could never have.
 *
 * The `brand-*` entries deliberately break it. They are **filled** marks in a shape the reader
 * already knows, because a brand mark's whole job is to be recognized at a glance: an Instagram
 * button that reads in this set's own stroke voice would be prettier and would stop saying
 * "Instagram". ⚠ **Do not "unify" them** - a brand mark redrawn to match the house style is a brand
 * mark that no longer identifies the brand, which is the one thing it is for. They are named with a
 * prefix so the difference is visible at every call site rather than being a surprise in the render.
 *
 * ## Accessibility is a real change here, not a detail
 *
 * An emoji carries an implicit accessible name that a screen reader announces; a bare `<svg>` carries
 * none, so a mechanical conversion makes the app QUIETER for a screen-reader user than the emoji were.
 * `<gh-icon>` therefore defaults to `aria-hidden` (right for the common case, where the label beside
 * the icon already says the thing) and takes an explicit `label` for the case where the icon IS the
 * only text. Neither is a default you get by forgetting; see `components/ghIcon.ts`.
 *
 * ## `GLYPH_ICONS`
 *
 * The map from each emoji this app used to draw to the icon that replaced it. It is not needed at
 * runtime - it exists so the conversion is mechanical and consistent (the same glyph became the same
 * icon in all 23 files), and so `tests/icons.test.ts` can tell a reader which icon a stray glyph
 * should have become instead of only saying "there is a glyph here". Keep it in step when an icon is
 * renamed; a glyph that is genuinely gone from the app stays listed, because the gate's job is to
 * catch it coming BACK.
 */

/** One drawing. `body` is the inner markup of a 24×24 `<svg>`; `filled` picks the brand-mark voice. */
export interface IconDef {
	body: string;
	filled?: boolean;
}

// The NAMES live in `shared/iconNames.ts` and are re-exported here so a frontend call site still has
// one import. ⚠ The split is not tidiness: a server payload names an icon (the dashboard's attention
// strip does), and `shared/` is the only place both sides may read - while the 101 path bodies below
// must never become reachable from a published page, which imports `shared/` freely.
export { ICON_NAMES, isIconName, type IconName } from "../../shared/iconNames.ts";
import { type IconName as IconNameT } from "../../shared/iconNames.ts";

/**
 * Every icon the app draws, by name.
 *
 * Names say what the picture IS, not what one caller uses it for: `target` rather than
 * `detect-sections`, because the same drawing serves the sections detector, the audio-match form and
 * the campaign angle, and a use-site name would be wrong at two of the three the day after it landed.
 */
const RAW = {
	/* ── status and feedback ─────────────────────────────────────────────────────────────────── */
	warning: {
		body: '<path d="M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9.5v4"/><path d="M12 17.2h.01"/>'
	},
	info: { body: '<circle cx="12" cy="12" r="9"/><path d="M12 11.2v5.3"/><path d="M12 7.6h.01"/>' },
	check: { body: '<path d="m4.5 12.5 5 5 10-11"/>' },
	"check-circle": { body: '<circle cx="12" cy="12" r="9"/><path d="m8 12.2 2.8 2.8L16 9.4"/>' },
	ban: { body: '<circle cx="12" cy="12" r="9"/><path d="m5.6 5.6 12.8 12.8"/>' },
	close: { body: '<path d="m6 6 12 12"/><path d="M18 6 6 18"/>' },
	flag: { body: '<path d="M5 21V3.5"/><path d="M5 4.5h11.5l-2.2 3.6 2.2 3.6H5"/>' },
	shield: { body: '<path d="M12 2.6 4.6 5.4v6c0 4.5 3 8 7.4 10 4.4-2 7.4-5.5 7.4-10v-6Z"/>' },
	party: {
		body: '<path d="m2.8 21.2 5.4-12.8 7.4 7.4Z"/><path d="M14.6 3.2v2.2"/><path d="m18.6 6.4 1.6-1.6"/><path d="M19.4 11h2.2"/><path d="M12.8 8.6a3.4 3.4 0 0 1 3.4-3.4"/>'
	},
	trophy: {
		body: '<path d="M8 3.5h8V9a4 4 0 0 1-8 0Z"/><path d="M8 5.2H5.2A2.8 2.8 0 0 0 8 9.6"/><path d="M16 5.2h2.8A2.8 2.8 0 0 1 16 9.6"/><path d="M12 13v3.6"/><path d="M9.4 16.6h5.2l1 3.9H8.4Z"/>'
	},
	star: { body: '<path d="m12 3 2.7 5.9 6.3.7-4.7 4.3 1.3 6.3L12 17.1l-5.6 3.1 1.3-6.3L3 9.6l6.3-.7Z"/>' },
	heart: { body: '<path d="M12 20.6S3.4 15 3.4 9.3A4.8 4.8 0 0 1 12 6.6a4.8 4.8 0 0 1 8.6 2.7c0 5.7-8.6 11.3-8.6 11.3Z"/>' },
	"thumbs-up": {
		body: '<path d="M7.4 20.6V9.6l4.4-6.9a2 2 0 0 1 3 2.2l-1.1 4h5.4a2 2 0 0 1 2 2.4l-1.6 7a2 2 0 0 1-2 1.6Z"/><rect x="2.6" y="9.6" width="4.8" height="11" rx="1"/>'
	},
	hand: {
		body: '<path d="M8.6 11V5.3a1.6 1.6 0 0 1 3.2 0v4.6"/><path d="M11.8 10.4V4.2a1.6 1.6 0 0 1 3.2 0v6.2"/><path d="M15 11V6.4a1.6 1.6 0 0 1 3.1 0V14a7 7 0 0 1-7 7 6 6 0 0 1-4.4-1.9l-2.6-2.8a1.7 1.7 0 0 1 2.5-2.3l1.9 2"/>'
	},

	// ⚠ The MIRROR of `thumbs-up`, not a new drawing: the two sit on the same rows (a pitch accepted,
	// a pitch passed on), and a down-thumb that is not the up-thumb flipped reads as a different
	// family of thing at 1em - the size at which this set is actually looked at.
	"thumbs-down": {
		body: '<path d="M16.6 3.4v11l-4.4 6.9a2 2 0 0 1-3-2.2l1.1-4H4.9a2 2 0 0 1-2-2.4l1.6-7a2 2 0 0 1 2-1.6Z"/><rect x="16.6" y="3.4" width="4.8" height="11" rx="1"/>'
	},
	question: { body: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.4a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.6-2.5 2.6"/><path d="M12 16.8h.01"/>' },
	bell: { body: '<path d="M18 8.8a6 6 0 1 0-12 0c0 4.9-2 6.4-2 6.4h16s-2-1.5-2-6.4Z"/><path d="M13.7 19a2 2 0 0 1-3.4 0"/>' },

	/* ── navigation and links ────────────────────────────────────────────────────────────────── */
	"arrow-up-right": { body: '<path d="M7 17 17 7"/><path d="M8.2 7H17v8.8"/>' },
	"arrow-down": { body: '<path d="M12 4v15"/><path d="m5.8 13 6.2 6 6.2-6"/>' },
	"arrow-up": { body: '<path d="M12 20V5"/><path d="m5.8 11 6.2-6 6.2 6"/>' },
	"arrow-back": { body: '<path d="M9 14.2 3.8 9 9 3.8"/><path d="M3.8 9h11.4a5 5 0 0 1 0 10h-3.6"/>' },
	link: {
		body: '<path d="M10.2 13.2a4.8 4.8 0 0 0 7.2.5l2.6-2.6a4.8 4.8 0 0 0-6.8-6.8l-1.5 1.5"/><path d="M13.8 10.8a4.8 4.8 0 0 0-7.2-.5L4 12.9a4.8 4.8 0 0 0 6.8 6.8l1.5-1.5"/>'
	},
	// ⚠ NOT `link` with an arrow bolted on: this one means "this leaves the app", so the box has a
	// corner deliberately missing - the reader is looking for the difference at a glance, in a row
	// where an in-app link sits beside it.
	"external-link": {
		body: '<path d="M13.8 3.6h6.6v6.6"/><path d="M20.4 3.6 11.4 12.6"/><path d="M18.4 13.8v4.6a2 2 0 0 1-2 2H5.6a2 2 0 0 1-2-2V7.6a2 2 0 0 1 2-2h4.6"/>'
	},
	share: {
		body: '<circle cx="18" cy="5.6" r="2.8"/><circle cx="6" cy="12" r="2.8"/><circle cx="18" cy="18.4" r="2.8"/><path d="m8.5 10.6 7-3.5"/><path d="m8.5 13.4 7 3.5"/>'
	},
	compass: { body: '<circle cx="12" cy="12" r="9"/><path d="m15.6 8.4-2.1 5.1-5.1 2.1 2.1-5.1Z"/>' },
	search: { body: '<circle cx="10.6" cy="10.6" r="6.6"/><path d="m15.4 15.4 5.1 5.1"/>' },
	filter: { body: '<path d="M3.4 5h17.2l-6.6 7.8v6.6l-4 1.6v-8.2Z"/>' },
	sort: { body: '<path d="M7 19.4V4.6"/><path d="m3.6 8 3.4-3.4L10.4 8"/><path d="M17 4.6v14.8"/><path d="m13.6 16 3.4 3.4 3.4-3.4"/>' },
	globe: { body: '<circle cx="12" cy="12" r="9"/><path d="M3.2 12h17.6"/><path d="M12 3a13.8 13.8 0 0 1 0 18 13.8 13.8 0 0 1 0-18Z"/>' },
	// ⚠ `globe` already means "the internet"; this one means "say it in another language", so it is
	// the standard glyph-plus-character pair rather than a second globe. The two are neighbours in
	// Settings (a hub domain, a UI language) and must not read alike.
	translate: {
		body: '<path d="M3.4 5.8h9"/><path d="M7.9 3.6v2.2"/><path d="M10.6 5.8c0 4.7-3 8.3-7.2 9.6"/><path d="M5.2 9.8c1.3 2.6 3.5 4.5 6.1 5.3"/><path d="m12.4 20.4 4.2-9.4 4.2 9.4"/><path d="M14 17.2h5.2"/>'
	},
	pin: { body: '<path d="M12 21.4s7-6.2 7-11.4a7 7 0 1 0-14 0c0 5.2 7 11.4 7 11.4Z"/><circle cx="12" cy="9.8" r="2.6"/>' },
	rocket: {
		body: '<path d="M12 2.6c3 2.5 4.6 5.9 4.6 9.6l-1.9 4.6H9.3l-1.9-4.6c0-3.7 1.6-7.1 4.6-9.6Z"/><circle cx="12" cy="9.8" r="2"/><path d="M9.3 16.8 6.6 19.6l3.2-.8"/><path d="m14.7 16.8 2.7 2.8-3.2-.8"/>'
	},
	plug: { body: '<path d="M9 2.6v6"/><path d="M15 2.6v6"/><path d="M6 8.6h12v2.2a6 6 0 0 1-12 0Z"/><path d="M12 16.8v4.6"/>' },
	gear: {
		body: '<circle cx="12" cy="12" r="3.4"/><path d="M19.1 14.6a1.5 1.5 0 0 0 .3 1.6l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-2.5 1.1v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-2.6-1.1l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0-1.1-2.5h-.2a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1.1-2.6l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.6.3h.1a1.5 1.5 0 0 0 .9-1.4v-.2a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 2.5 1.1l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0 1.1 2.5h.2a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9Z"/>'
	},

	home: { body: '<path d="M3.6 10.4 12 3.4l8.4 7v9a1.6 1.6 0 0 1-1.6 1.6H5.2a1.6 1.6 0 0 1-1.6-1.6Z"/><path d="M9.4 21V14.2h5.2V21"/>' },
	// The dashboard's mark. ⚠ A `chart-bar` would have done and is wrong: the dashboard is not a
	// chart, it is the one screen that says how the whole thing is doing, and the chart icon is
	// already spoken for by the analytics panels it sits above.
	gauge: {
		body: '<path d="M3.6 18a9 9 0 1 1 16.8 0"/><path d="m12.8 15.2 3.6-4.6"/><circle cx="12" cy="18" r="1.5" fill="currentColor" stroke="none"/>'
	},
	grid: {
		body: '<rect x="3.4" y="3.4" width="7.2" height="7.2" rx="1.6"/><rect x="13.4" y="3.4" width="7.2" height="7.2" rx="1.6"/><rect x="3.4" y="13.4" width="7.2" height="7.2" rx="1.6"/><rect x="13.4" y="13.4" width="7.2" height="7.2" rx="1.6"/>'
	},
	list: {
		body: '<path d="M8.6 6.4h11.8"/><path d="M8.6 12h11.8"/><path d="M8.6 17.6h11.8"/><circle cx="4.4" cy="6.4" r="1.3" fill="currentColor" stroke="none"/><circle cx="4.4" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="4.4" cy="17.6" r="1.3" fill="currentColor" stroke="none"/>'
	},
	"list-check": {
		body: '<path d="M10.4 6.4h10"/><path d="M10.4 12h10"/><path d="M10.4 17.6h10"/><path d="m3.2 6.2 1.6 1.6 2.6-2.8"/><path d="m3.2 11.8 1.6 1.6 2.6-2.8"/><path d="m3.2 17.4 1.6 1.6 2.6-2.8"/>'
	},
	menu: { body: '<path d="M3.6 6.6h16.8"/><path d="M3.6 12h16.8"/><path d="M3.6 17.4h16.8"/>' },
	dots: {
		body: '<circle cx="5.4" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="18.6" cy="12" r="1.5" fill="currentColor" stroke="none"/>'
	},
	// ⚠ A chevron is the one drawing whose job is to be nearly invisible, so it is drawn LIGHTER than
	// the metaphor icons by being shorter, not by changing stroke width - one weight for the set is
	// the whole identity (see the header), and a 1.25 chevron would be the first crack in it.
	"chevron-down": { body: '<path d="m5.8 9 6.2 6.2L18.2 9"/>' },
	"chevron-right": { body: '<path d="m9 5.8 6.2 6.2L9 18.2"/>' },

	/* ── media and playback ──────────────────────────────────────────────────────────────────── */
	play: { body: '<path d="M8.4 5.4v13.2L19 12Z"/>' },
	pause: { body: '<path d="M9 5v14"/><path d="M15 5v14"/>' },
	stop: { body: '<rect x="5" y="5" width="14" height="14" rx="2.2"/>' },
	repeat: {
		body: '<path d="M4 9.4V8.6a3 3 0 0 1 3-3h12.4"/><path d="m16.6 2.6 2.8 3-2.8 3"/><path d="M20 14.6v.8a3 3 0 0 1-3 3H4.6"/><path d="m7.4 21.4-2.8-3 2.8-3"/>'
	},
	shuffle: {
		body: '<path d="M3 6.4h3.4l11.2 11.2H21"/><path d="m18.2 14.8 2.8 2.8-2.8 2.8"/><path d="M3 17.6h3.4l2.9-2.9"/><path d="m14.7 9.3 2.9-2.9H21"/><path d="m18.2 3.6 2.8 2.8-2.8 2.8"/>'
	},
	mic: { body: '<rect x="9" y="2.6" width="6" height="11" rx="3"/><path d="M5.4 11.6a6.6 6.6 0 0 0 13.2 0"/><path d="M12 18.2v3.2"/>' },
	headphones: {
		body: '<path d="M4.2 15.4V13a7.8 7.8 0 0 1 15.6 0v2.4"/><path d="M4.2 14.6H6a1.6 1.6 0 0 1 1.6 1.6v2.6A1.6 1.6 0 0 1 6 20.4h-.2a1.6 1.6 0 0 1-1.6-1.6Z"/><path d="M19.8 14.6H18a1.6 1.6 0 0 0-1.6 1.6v2.6a1.6 1.6 0 0 0 1.6 1.6h.2a1.6 1.6 0 0 0 1.6-1.6Z"/>'
	},
	"music-note": {
		body: '<path d="M9 17.6V5.6l10.6-2v12"/><ellipse cx="6.4" cy="17.8" rx="2.6" ry="2.2"/><ellipse cx="17" cy="15.6" rx="2.6" ry="2.2"/>'
	},
	"music-notes": {
		body: '<path d="M9 17.6V5.6l10.6-2v12"/><ellipse cx="6.4" cy="17.8" rx="2.6" ry="2.2"/><ellipse cx="17" cy="15.6" rx="2.6" ry="2.2"/><path d="M9 9.4 19.6 7.4"/>'
	},
	disc: { body: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2.6"/>' },
	volume: {
		body: '<path d="M4 9.4h3.4L12 5.2v13.6l-4.6-4.2H4Z"/><path d="M15.6 9.6a3.4 3.4 0 0 1 0 4.8"/><path d="M18.2 7a7 7 0 0 1 0 10"/>'
	},
	// ⚠ Not `chart-line`: this is a heartbeat, and it means "something is happening right now" -
	// the momentum lane and a live sweep, never a measurement over a period.
	pulse: { body: '<path d="M2.6 12h4l2.6-7.2 4.4 14.4 2.6-7.2h5.2"/>' },
	film: { body: '<rect x="2.6" y="5" width="18.8" height="14" rx="2.2"/><path d="M7.4 5v14"/><path d="M16.6 5v14"/><path d="M2.6 12h18.8"/>' },
	clapper: {
		body: '<path d="M3.4 9.8h17.2V19a2 2 0 0 1-2 2H5.4a2 2 0 0 1-2-2Z"/><path d="m3.6 6.4 15.6-3.1.9 3.6-15.6 3.1Z"/><path d="m8.7 5.4.9 3.6"/><path d="m13.8 4.4.9 3.6"/>'
	},
	"video-camera": { body: '<rect x="2.6" y="6.2" width="12.8" height="11.6" rx="2.2"/><path d="m15.4 10.6 6-3.4v9.6l-6-3.4Z"/>' },
	camera: {
		body: '<path d="M4 7.6h3l1.6-2.4h6.8L17 7.6h3a1.6 1.6 0 0 1 1.6 1.6v8.6A1.6 1.6 0 0 1 20 19.4H4a1.6 1.6 0 0 1-1.6-1.6V9.2A1.6 1.6 0 0 1 4 7.6Z"/><circle cx="12" cy="13.2" r="3.6"/>'
	},
	image: {
		body: '<rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.2"/><circle cx="8.8" cy="9.6" r="1.8"/><path d="m3.8 17.6 4.8-4.6a2 2 0 0 1 2.8 0l6.2 6"/>'
	},
	scissors: {
		body: '<circle cx="6.4" cy="6.4" r="2.6"/><circle cx="6.4" cy="17.6" r="2.6"/><path d="m8.6 8.2 11.8 11.2"/><path d="m8.6 15.8 11.8-11.2"/>'
	},
	crop: { body: '<path d="M6.4 2.6v14.6a2 2 0 0 0 2 2h13"/><path d="M2.6 6.4h14.6a2 2 0 0 1 2 2v13"/>' },
	eye: { body: '<path d="M2.4 12S6 5.6 12 5.6 21.6 12 21.6 12 18 18.4 12 18.4 2.4 12 2.4 12Z"/><circle cx="12" cy="12" r="3"/>' },
	waves: {
		body: '<path d="M2.6 7.6c1.9-2 3.8-2 5.7 0s3.8 2 5.7 0 3.8-2 5.7 0"/><path d="M2.6 12.4c1.9-2 3.8-2 5.7 0s3.8 2 5.7 0 3.8-2 5.7 0"/><path d="M2.6 17.2c1.9-2 3.8-2 5.7 0s3.8 2 5.7 0 3.8-2 5.7 0"/>'
	},
	"wave-line": { body: '<path d="M2.6 12c2-3.4 3.9-3.4 5.9 0s3.9 3.4 5.9 0 3.9-3.4 5.9 0"/>' },
	lungs: {
		body: '<path d="M12 3v9.4"/><path d="M12 12.4c0-1.5-.8-2.6-2-3.5-1.8-1.3-4-.2-4.6 2l-1.3 5a3 3 0 0 0 2.9 3.7h1.5a3.5 3.5 0 0 0 3.5-3.5Z"/><path d="M12 12.4c0-1.5.8-2.6 2-3.5 1.8-1.3 4-.2 4.6 2l1.3 5a3 3 0 0 1-2.9 3.7h-1.5a3.5 3.5 0 0 1-3.5-3.5Z"/>'
	},
	fog: {
		body: '<path d="M3.6 8.6h11"/><path d="M17.6 8.6h2.8"/><path d="M3.6 12.4h5.6"/><path d="M12.2 12.4h8.2"/><path d="M5.4 16.2h9.2"/><path d="M17.4 16.2h3"/>'
	},
	sunrise: {
		body: '<path d="M12 3v3.6"/><path d="m5.6 9.2-1.5-1.5"/><path d="M2.6 15.6h2.6"/><path d="M18.8 15.6h2.6"/><path d="m19.9 7.7-1.5 1.5"/><path d="M8 15.6a4 4 0 0 1 8 0"/><path d="M2.6 19.6h18.8"/>'
	},
	bridge: {
		body: '<path d="M2.6 19.4h18.8"/><path d="M2.6 12.2a18.6 18.6 0 0 1 18.8 0"/><path d="M6.4 19.4v-4.8"/><path d="M12 19.4v-7.6"/><path d="M17.6 19.4v-4.8"/>'
	},

	/* ── the workshop: editing, rendering, measuring ─────────────────────────────────────────── */
	target: {
		body: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.8"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/>'
	},
	sliders: {
		body: '<path d="M6 3.4v4.2"/><path d="M6 12.6v8"/><circle cx="6" cy="10.1" r="2.5"/><path d="M12 3.4v9.4"/><path d="M12 17.8v2.6"/><circle cx="12" cy="15.3" r="2.5"/><path d="M18 3.4v2.2"/><path d="M18 10.6v9.8"/><circle cx="18" cy="8.1" r="2.5"/>'
	},
	sparkles: {
		body: '<path d="M11.4 3.4 13 8.1l4.7 1.6-4.7 1.6-1.6 4.7-1.6-4.7L5.1 9.7 9.8 8.1Z"/><path d="m18.2 15.2.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8Z"/>'
	},
	palette: {
		body: '<path d="M12 21a9 9 0 1 1 9-9c0 2-1.7 3-3.3 3H16a2 2 0 0 0-1.4 3.4A1.9 1.9 0 0 1 12 21Z"/><circle cx="7.6" cy="12.4" r="1.2" fill="currentColor" stroke="none"/><circle cx="9.4" cy="7.9" r="1.2" fill="currentColor" stroke="none"/><circle cx="14.4" cy="7.4" r="1.2" fill="currentColor" stroke="none"/><circle cx="17.2" cy="10.9" r="1.2" fill="currentColor" stroke="none"/>'
	},
	angle: { body: '<path d="M20.4 20.4H3.6V3.6Z"/><path d="M9.4 20.4A10.6 10.6 0 0 0 3.6 11"/>' },
	pencil: { body: '<path d="M4 20h4.2L20 8.2a3 3 0 0 0-4.2-4.2L4 15.8Z"/><path d="m14.6 5.4 4 4"/>' },
	write: { body: '<path d="M4 20h4.2L20 8.2a3 3 0 0 0-4.2-4.2L4 15.8Z"/><path d="m14.6 5.4 4 4"/><path d="M3 22.2h18"/>' },
	// ⚠ The lid is a SEPARATE path from the body and the handle is a third, because at 1em a
	// one-path bin loses its lid line entirely and reads as a cup.
	trash: {
		body: '<path d="M3.8 6.6h16.4"/><path d="M9.4 6.6V4.6a1.2 1.2 0 0 1 1.2-1.2h2.8a1.2 1.2 0 0 1 1.2 1.2v2"/><path d="M6.4 6.6 7.2 20a1.6 1.6 0 0 0 1.6 1.5h6.4a1.6 1.6 0 0 0 1.6-1.5l.8-13.4"/><path d="M10.4 10.6v6.4"/><path d="M13.6 10.6v6.4"/>'
	},
	plus: { body: '<path d="M12 4.8v14.4"/><path d="M4.8 12h14.4"/>' },
	minus: { body: '<path d="M4.8 12h14.4"/>' },
	copy: {
		body: '<rect x="8.6" y="8.6" width="12" height="12" rx="2"/><path d="M15.4 5.6a2 2 0 0 0-2-2H5.4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2"/>'
	},
	// ⚠ TWO arcs, not `recycle`'s one. `recycle` means "do it again from the start" (a re-scrape, a
	// re-draft); this means "go and look at the source again", and the pair of arrows is the only
	// thing carrying that difference at this size.
	refresh: {
		body: '<path d="M3.8 12a8.2 8.2 0 0 1 13.9-5.9l2.5 2.4"/><path d="M20.2 3.8v4.7h-4.7"/><path d="M20.2 12a8.2 8.2 0 0 1-13.9 5.9l-2.5-2.4"/><path d="M3.8 20.2v-4.7h4.7"/>'
	},
	// ⚠ A bare arrow and a FLOOR, deliberately not `inbox`/`send`'s tray: those two are about mail,
	// and a file leaving or joining the app is a different sentence that happens to point the same way.
	download: { body: '<path d="M12 3.4v11.4"/><path d="m6.9 9.8 5.1 5 5.1-5"/><path d="M4.4 20.6h15.2"/>' },
	upload: { body: '<path d="M12 15.2V3.8"/><path d="m6.9 8.8 5.1-5 5.1 5"/><path d="M4.4 20.6h15.2"/>' },
	type: { body: '<path d="M4 6.6V5h16v1.6"/><path d="M12 5v14.4"/><path d="M8.8 19.4h6.4"/>' },
	flask: { body: '<path d="M9.4 3v6.4L4.5 18a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3l-4.9-8.6V3"/><path d="M8 3h8"/><path d="M7 14.4h10"/>' },
	bolt: { body: '<path d="M13.2 2.6 4.6 13.8H11l-1 7.6 8.4-11.2H12Z"/>' },
	bulb: {
		body: '<path d="M9.2 18.2h5.6"/><path d="M10.4 21h3.2"/><path d="M12 2.6a6.2 6.2 0 0 0-3.6 11.2c.7.5 1.1 1.3 1.2 2.2h4.8c.1-.9.5-1.7 1.2-2.2A6.2 6.2 0 0 0 12 2.6Z"/>'
	},
	clock: { body: '<circle cx="12" cy="12" r="9"/><path d="M12 6.8v5.5l3.4 2"/>' },
	stopwatch: { body: '<circle cx="12" cy="13.6" r="7.6"/><path d="M12 9.6v4l2.6 2"/><path d="M9.4 2.6h5.2"/><path d="M12 2.6v3.4"/>' },
	hourglass: { body: '<path d="M6 3h12"/><path d="M6 21h12"/><path d="M17 3v3.6L12 12l5 5.4V21"/><path d="M7 3v3.6L12 12l-5 5.4V21"/>' },
	calendar: {
		body: '<rect x="3.4" y="5.4" width="17.2" height="15.2" rx="2.2"/><path d="M3.4 10.2h17.2"/><path d="M8.2 3.4v4"/><path d="M15.8 3.4v4"/>'
	},
	sun: {
		body: '<circle cx="12" cy="12" r="4.2"/><path d="M12 2.8v2.2"/><path d="M12 19v2.2"/><path d="m5.4 5.4 1.6 1.6"/><path d="m17 17 1.6 1.6"/><path d="M2.8 12H5"/><path d="M19 12h2.2"/><path d="m5.4 18.6 1.6-1.6"/><path d="m17 7 1.6-1.6"/>'
	},
	moon: { body: '<path d="M20.4 13.8A8.8 8.8 0 0 1 10.2 3.6a8.8 8.8 0 1 0 10.2 10.2Z"/>' },

	/* ── documents and records ───────────────────────────────────────────────────────────────── */
	folder: {
		body: '<path d="M3 7.6a2 2 0 0 1 2-2h3.8l2.2 2.6H19a2 2 0 0 1 2 2v7.2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>'
	},
	file: { body: '<path d="M14 2.6H7a2 2 0 0 0-2 2v14.8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.6Z"/><path d="M14 2.6v5h5"/>' },
	note: {
		body: '<path d="M14 2.6H7a2 2 0 0 0-2 2v14.8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.6Z"/><path d="M14 2.6v5h5"/><path d="M8.6 12.4h6.8"/><path d="M8.6 16h4.6"/>'
	},
	journal: { body: '<path d="M7 2.6h11a1 1 0 0 1 1 1v17.8H7a3 3 0 0 1-3-3V5.6a3 3 0 0 1 3-3Z"/><path d="M4 17.6h15"/><path d="M9.4 2.6v14.4"/>' },
	book: {
		body: '<path d="M12 6.6S10 4 5.6 4H3v14h2.6c4.4 0 6.4 2.4 6.4 2.4"/><path d="M12 6.6S14 4 18.4 4H21v14h-2.6c-4.4 0-6.4 2.4-6.4 2.4"/><path d="M12 6.6v13.8"/>'
	},
	clipboard: {
		body: '<path d="M9 4.2H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-13a2 2 0 0 0-2-2h-2"/><rect x="9" y="2.6" width="6" height="3.8" rx="1.2"/>'
	},
	archive: {
		body: '<rect x="2.6" y="4" width="18.8" height="4.8" rx="1.2"/><path d="M4.6 8.8v10.4a1.4 1.4 0 0 0 1.4 1.4h12a1.4 1.4 0 0 0 1.4-1.4V8.8"/><path d="M10 13h4"/>'
	},
	bookmark: { body: '<path d="M6.6 2.6h10.8a1 1 0 0 1 1 1v17.8L12 16.8l-6.4 4.6V3.6a1 1 0 0 1 1-1Z"/>' },
	tag: {
		body: '<path d="M3.4 10.6V4.4a1 1 0 0 1 1-1h6.2l9.4 9.4a1.5 1.5 0 0 1 0 2.1l-5.5 5.5a1.5 1.5 0 0 1-2.1 0Z"/><circle cx="7.8" cy="7.8" r="1.5"/>'
	},
	paperclip: { body: '<path d="M20 11.6 12 19.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.4 3.4 0 0 1 4.8 4.8l-8.6 8.6a1.8 1.8 0 0 1-2.5-2.5l7.9-7.9"/>' },
	// The OUTLET's mark: a blog, a magazine, a station. ⚠ The fold on the right is what stops it
	// reading as `note` - an outlet is a publication, and the app's whole outreach half points at it.
	newspaper: {
		body: '<path d="M3.4 6.4a2 2 0 0 1 2-2h9.2a2 2 0 0 1 2 2v13a2 2 0 0 0 2 2H5.4a2 2 0 0 1-2-2Z"/><path d="M16.6 8.6h2a2 2 0 0 1 2 2v8.8a2 2 0 0 1-2 2"/><path d="M6.6 8.4h6.6"/><path d="M6.6 12h6.6"/><path d="M6.6 15.6h4.2"/>'
	},
	"chart-bar": { body: '<path d="M4 3.6v16.8h16.4"/><path d="M8.4 17.2v-5.4"/><path d="M12.8 17.2V7.6"/><path d="M17.2 17.2V9.8"/>' },
	"chart-line": { body: '<path d="M4 3.6v16.8h16.4"/><path d="m7.6 15 3.6-4.2 3 3 5-6.2"/>' },

	/* ── mail and outreach ───────────────────────────────────────────────────────────────────── */
	mail: { body: '<rect x="2.6" y="5" width="18.8" height="14" rx="2.2"/><path d="m3.2 7 8 5.6a1.6 1.6 0 0 0 1.6 0l8-5.6"/>' },
	mailbox: {
		body: '<path d="M3 12.2a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4V19a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z"/><path d="M11 20V8.2"/><path d="M17 8.2V4.2h-3.2"/><path d="M6.2 13.4h2.4"/>'
	},
	postbox: {
		body: '<rect x="3.4" y="6.6" width="17.2" height="13" rx="2.2"/><path d="M7.6 10.6h8.8"/><path d="M12 6.6V3.4"/><path d="M8.6 21.4h6.8"/>'
	},
	send: { body: '<path d="M12 3.2v12"/><path d="m7 8.2 5-5 5 5"/><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"/>' },
	inbox: { body: '<path d="M12 21.2V9"/><path d="m7 16.2 5 5 5-5"/><path d="M4 9V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/>' },
	comment: { body: '<path d="M20.4 15.2a2 2 0 0 1-2 2H8.2l-4.6 4V5.6a2 2 0 0 1 2-2h12.8a2 2 0 0 1 2 2Z"/>' },
	megaphone: {
		body: '<path d="M3.6 9.8v4.4a2 2 0 0 0 2 2h1.2l9.4 4V3.8l-9.4 4H5.6a2 2 0 0 0-2 2Z"/><path d="M19.4 9a3.6 3.6 0 0 1 0 6"/><path d="M7.8 16.2v3.4a1.6 1.6 0 0 0 3.2 0v-2"/>'
	},
	users: {
		body: '<path d="M15.6 20.4v-1.6a4.2 4.2 0 0 0-4.2-4.2H7.2A4.2 4.2 0 0 0 3 18.8v1.6"/><circle cx="9.3" cy="7.4" r="3.6"/><path d="M21 20.4v-1.6a4.2 4.2 0 0 0-3.2-4.1"/><path d="M15.4 4a3.6 3.6 0 0 1 0 7"/>'
	},
	user: { body: '<path d="M19 20.4v-1.6a5 5 0 0 0-5-5h-4a5 5 0 0 0-5 5v1.6"/><circle cx="12" cy="7.4" r="3.8"/>' },

	/* ── money ───────────────────────────────────────────────────────────────────────────────── */
	money: {
		body: '<circle cx="12" cy="12" r="9"/><path d="M15 9.2a3.2 3.2 0 0 0-2.8-1.4c-1.6 0-2.8.9-2.8 2.1s1.1 1.9 2.8 2.2 2.9 1 2.9 2.2-1.2 2.1-2.9 2.1A3.2 3.2 0 0 1 9.2 15"/><path d="M12 6.2v11.6"/>'
	},
	// ⚠ `money` is a COIN - an amount, a price, a budget. This is the act of PAYING, and the two are
	// a card's width apart in the billing card: "you are on the €19 plan" vs "add a card".
	"credit-card": { body: '<rect x="2.6" y="5" width="18.8" height="14" rx="2.4"/><path d="M2.6 9.8h18.8"/><path d="M6.4 15h3.4"/>' },
	gift: {
		body: '<rect x="3" y="8.4" width="18" height="4.2" rx="1.2"/><path d="M4.6 12.6v7.4a1 1 0 0 0 1 1h12.8a1 1 0 0 0 1-1v-7.4"/><path d="M12 8.4v12.6"/><path d="M12 8.4S10.6 3 8.2 3a2.7 2.7 0 0 0 0 5.4Z"/><path d="M12 8.4S13.4 3 15.8 3a2.7 2.7 0 0 1 0 5.4Z"/>'
	},
	recycle: {
		body: '<path d="M20.6 12a8.6 8.6 0 1 1-2.5-6.1"/><path d="M20.6 3.8v5.4h-5.4"/>'
	},

	building: {
		body: '<path d="M4.4 21V4.6a1.2 1.2 0 0 1 1.2-1.2h8.8a1.2 1.2 0 0 1 1.2 1.2V21"/><path d="M15.6 10.6h3a1.2 1.2 0 0 1 1.2 1.2V21"/><path d="M2.6 21h18.8"/><path d="M8 8h4"/><path d="M8 12.4h4"/><path d="M8 16.8h4"/>'
	},

	/* ── devices and machinery ───────────────────────────────────────────────────────────────── */
	phone: { body: '<rect x="6" y="2.6" width="12" height="18.8" rx="2.6"/><path d="M10.4 18.4h3.2"/>' },
	// The Devices card's mark for a session we could not name a browser for (2026-08-28). The lid is
	// the same rounded rectangle as `phone` on its side, which is the point - the two are read as a
	// pair, one row apart, and the base bar is the only thing that has to do the distinguishing.
	laptop: { body: '<rect x="3.4" y="4.6" width="17.2" height="11.4" rx="2"/><path d="M1.8 19.4h20.4"/>' },
	// Where a render runs when it is not this device (2026-09-01): the render progress card draws
	// `laptop`/`phone` for the artist's own machine and this for the server. One closed outline, the
	// same weight as its neighbours - a cloud with lines in it is a weather glyph.
	cloud: { body: '<path d="M18.2 10.4h-1.3a7.6 7.6 0 1 0-7.4 9.4h8.7a4.7 4.7 0 0 0 0-9.4z"/>' },
	robot: {
		body: '<rect x="3.6" y="7.6" width="16.8" height="12.4" rx="3.2"/><circle cx="9" cy="13.2" r="1.4" fill="currentColor" stroke="none"/><circle cx="15" cy="13.2" r="1.4" fill="currentColor" stroke="none"/><path d="M12 4.4v3.2"/><circle cx="12" cy="3" r="1.4"/><path d="M3.6 12.6H2.2"/><path d="M20.4 12.6h1.4"/>'
	},
	save: {
		body: '<path d="M5 3.4h11L19.6 7v13.4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4.4a1 1 0 0 1 1-1Z"/><path d="M8 3.4v5.2h7V3.4"/><path d="M7 14h10v7.4H7Z"/>'
	},

	lock: { body: '<rect x="4.4" y="10.2" width="15.2" height="10.8" rx="2.2"/><path d="M8 10.2V7.6a4 4 0 0 1 8 0v2.6"/>' },
	// ⚠ `lock` means "this is protected"; `key` means "you hold the credential" - the pasted API key,
	// the app secret. They sit on the same Settings cards and a single drawing for both would make
	// "your key is saved" and "this is encrypted" the same sentence.
	key: { body: '<circle cx="7.6" cy="16.4" r="3.8"/><path d="m10.4 13.6 8.6-8.6"/><path d="m15.6 8.4 2.2 2.2"/><path d="m17.8 6.2 2.4 2.4"/>' },

	/* ── brand marks (a second visual voice, on purpose - see the header) ────────────────────── */
	/**
	 * ⚠ Spotify is a brand mark this app needed for 180 rounds and never had, because round 145's
	 * census could only convert glyphs that EXISTED - and the surfaces that talk to Spotify (quick
	 * start's catalog picker, the playlist trust audit, the streaming destinations) had never used
	 * one. **A conversion round can only ever be as complete as the thing it converted**, which is the
	 * general reason this file grew again: the emoji set was the app's icon vocabulary, so anywhere the
	 * app had no emoji it still has no icon, and that absence looks exactly like a decision.
	 */
	"brand-spotify": {
		// ⚠ **This is Spotify's OWN mark, not a drawing of it** (owner report, 2026-08-07: *"the logo is
		// skewed to the left from the center"*), and they were right - measurably. The hand-drawn version
		// chained its four subpaths with RELATIVE `m` commands, so each wave's origin was an offset from
		// where the previous one happened to end rather than a position in the box. The three waves came
		// out centered at x≈10.25, 10.5 and 11.1 inside a circle centered at 12 - leaning left, worst at
		// the bottom, which is exactly what the eye reports before it can say why.
		//
		// ⚠ **The general rule: a chain of relative subpaths has no anchor.** Every `m` after a `Z`
		// measures from the previous subpath's start, so one wrong number silently shifts everything
		// after it and the drawing still looks like a plausible logo. For a mark that is somebody's
		// trademark, an approximation is also just wrong on its own terms - so this is the official
		// geometry, copied from `simple-icons` (already a dependency, and where `streamingBrands.ts`
		// gets the same mark for the published pages). ⚠ Inlined as a literal rather than imported,
		// because `icons.ts` bodies must never become reachable from a published-page renderer - that
		// is this file's own standing rule and an import here would be the crack in it.
		body: '<path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>',
		filled: true
	},
	/**
	 * ⚠ Apple Music is drawn as its NOTE, not as the apple. The apple is Apple the company and it turns
	 * up beside a sign-in button; what a row in the artist finder means is the music store, which is the
	 * note in its own rounded tile - the same distinction `lock` and `key` make two sections up. Drawn as
	 * a beamed pair of notes inside a rounded square, so it reads at 16px where a bare glyph does not.
	 */
	"brand-apple-music": {
		// ⚠ `fill-rule="evenodd"` is load-bearing, not decoration: the note is a SUBPATH inside the tile,
		// and under the default nonzero rule it fills solid instead of cutting a hole - which renders as
		// a plain filled square that reads as a missing icon. The other brand marks with an inner shape
		// reverse the arc SWEEP FLAG to get the same effect; this one has too many arcs for that to stay
		// legible, so the rule says it once.
		body: '<path fill-rule="evenodd" d="M5.6 2.4h12.8a3.2 3.2 0 0 1 3.2 3.2v12.8a3.2 3.2 0 0 1-3.2 3.2H5.6a3.2 3.2 0 0 1-3.2-3.2V5.6a3.2 3.2 0 0 1 3.2-3.2Zm10.2 3.3-6.6 1.4a.9.9 0 0 0-.7.9v6.4a2 2 0 1 0 1.6 1.9V9.6l5-1.1v4.3a2 2 0 1 0 1.6 1.9V6.6a.9.9 0 0 0-1-.9Z"/>',
		filled: true
	},
	/**
	 * ⚠ SoundCloud's mark is the waveform-into-a-cloud, and the bars are the half that identifies it -
	 * a cloud alone is every storage product there is. Five bars of rising height with the cloud lobe on
	 * the right, which is the silhouette the real mark has at any size.
	 */
	"brand-soundcloud": {
		body: '<path d="M1.8 11.2c.3 0 .5.2.5.5l.3 3-.3 3a.5.5 0 0 1-1 0l-.3-3 .3-3c0-.3.2-.5.5-.5Zm2.6-1.4c.3 0 .5.2.5.5l.4 4.4-.4 4.2a.5.5 0 0 1-1 0l-.3-4.2.3-4.4c0-.3.2-.5.5-.5Zm2.7-1.2c.3 0 .6.2.6.6l.3 5.5-.3 4.1a.6.6 0 0 1-1.2 0l-.3-4.1.3-5.5c0-.4.3-.6.6-.6Zm2.8-1.3c.4 0 .6.3.6.6l.3 6.8-.3 4a.6.6 0 0 1-1.2 0l-.3-4 .3-6.8c0-.3.2-.6.6-.6Zm2.9-1.9c.4 0 .7.3.7.7l.3 8.7-.3 3.9a.7.7 0 0 1-1.4 0l-.2-3.9.2-8.7c0-.4.3-.7.7-.7Zm2.6 2.3a4 4 0 0 1 1.7-.4 4.3 4.3 0 0 1 4.3 4.1 3 3 0 0 1-.7 5.9h-5.3a.8.8 0 0 1-.8-.8V8.3c0-.3.2-.5.4-.6Z"/>',
		filled: true
	},
	/**
	 * ⚠ Deezer is bars in a grid and nothing else - no letter, no circle - so the SHAPE of the grid is
	 * the whole identification: a stepped block, one bar on the top row, three on the next, five below.
	 * Drawn as filled rects rather than a path, which is what it is.
	 */
	"brand-deezer": {
		body: '<rect x="17.4" y="5.6" width="4.4" height="2.6" rx="0.5"/><rect x="10.2" y="9.7" width="4.4" height="2.6" rx="0.5"/><rect x="17.4" y="9.7" width="4.4" height="2.6" rx="0.5"/><rect x="3" y="13.8" width="4.4" height="2.6" rx="0.5"/><rect x="10.2" y="13.8" width="4.4" height="2.6" rx="0.5"/><rect x="17.4" y="13.8" width="4.4" height="2.6" rx="0.5"/><rect x="3" y="17.9" width="4.4" height="2.6" rx="0.5"/><rect x="10.2" y="17.9" width="4.4" height="2.6" rx="0.5"/><rect x="17.4" y="17.9" width="4.4" height="2.6" rx="0.5"/>',
		filled: true
	},
	/**
	 * The browser marks (2026-08-28, the Devices card). Official geometry from `simple-icons`, inlined
	 * as literals for the reason `brand-google` states: a trademark drawn by hand is wrong on its own
	 * terms, and an import here would be the crack in this file's rule that its bodies never become
	 * reachable from a published-page renderer.
	 *
	 * ⚠ **The set is deliberately incomplete and `shared/userAgent.ts` is built for that** - Edge has
	 * no shippable mark (Microsoft had it removed from `simple-icons`), Samsung Internet has none
	 * either, and Safari's is 11.7KB of geometry against a bundle round 279 spent a whole round cutting.
	 * Each of those draws the device's own glyph beside its correct NAME, which is the honest half.
	 */
	"brand-chrome": {
		body: '<path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-5.344 9.257c.206.01.413.016.621.016 6.627 0 12-5.373 12-12 0-1.54-.29-3.011-.818-4.364zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728Z"/>',
		filled: true
	},
	"brand-firefox": {
		body: '<path d="M8.824 7.287c.008 0 .004 0 0 0zm-2.8-1.4c.006 0 .003 0 0 0zm16.754 2.161c-.505-1.215-1.53-2.528-2.333-2.943.654 1.283 1.033 2.57 1.177 3.53l.002.02c-1.314-3.278-3.544-4.6-5.366-7.477-.091-.147-.184-.292-.273-.446a3.545 3.545 0 01-.13-.24 2.118 2.118 0 01-.172-.46.03.03 0 00-.027-.03.038.038 0 00-.021 0l-.006.001a.037.037 0 00-.01.005L15.624 0c-2.585 1.515-3.657 4.168-3.932 5.856a6.197 6.197 0 00-2.305.587.297.297 0 00-.147.37c.057.162.24.24.396.17a5.622 5.622 0 012.008-.523l.067-.005a5.847 5.847 0 011.957.222l.095.03a5.816 5.816 0 01.616.228c.08.036.16.073.238.112l.107.055a5.835 5.835 0 01.368.211 5.953 5.953 0 012.034 2.104c-.62-.437-1.733-.868-2.803-.681 4.183 2.09 3.06 9.292-2.737 9.02a5.164 5.164 0 01-1.513-.292 4.42 4.42 0 01-.538-.232c-1.42-.735-2.593-2.121-2.74-3.806 0 0 .537-2 3.845-2 .357 0 1.38-.998 1.398-1.287-.005-.095-2.029-.9-2.817-1.677-.422-.416-.622-.616-.8-.767a3.47 3.47 0 00-.301-.227 5.388 5.388 0 01-.032-2.842c-1.195.544-2.124 1.403-2.8 2.163h-.006c-.46-.584-.428-2.51-.402-2.913-.006-.025-.343.176-.389.206-.406.29-.787.616-1.136.974-.397.403-.76.839-1.085 1.303a9.816 9.816 0 00-1.562 3.52c-.003.013-.11.487-.19 1.073-.013.09-.026.181-.037.272a7.8 7.8 0 00-.069.667l-.002.034-.023.387-.001.06C.386 18.795 5.593 24 12.016 24c5.752 0 10.527-4.176 11.463-9.661.02-.149.035-.298.052-.448.232-1.994-.025-4.09-.753-5.844z"/>',
		filled: true
	},
	"brand-opera": {
		body: '<path d="M8.051 5.238c-1.328 1.566-2.186 3.883-2.246 6.48v.564c.061 2.598.918 4.912 2.246 6.479 1.721 2.236 4.279 3.654 7.139 3.654 1.756 0 3.4-.537 4.807-1.471C17.879 22.846 15.074 24 12 24c-.192 0-.383-.004-.57-.014C5.064 23.689 0 18.436 0 12 0 5.371 5.373 0 12 0h.045c3.055.012 5.84 1.166 7.953 3.055-1.408-.93-3.051-1.471-4.81-1.471-2.858 0-5.417 1.42-7.14 3.654h.003zM24 12c0 3.556-1.545 6.748-4.002 8.945-3.078 1.5-5.946.451-6.896-.205 3.023-.664 5.307-4.32 5.307-8.74 0-4.422-2.283-8.075-5.307-8.74.949-.654 3.818-1.703 6.896-.205C22.455 5.25 24 8.445 24 12z"/>',
		filled: true
	},
	"brand-instagram": {
		body: '<path d="M7.6 2.4h8.8a5.2 5.2 0 0 1 5.2 5.2v8.8a5.2 5.2 0 0 1-5.2 5.2H7.6a5.2 5.2 0 0 1-5.2-5.2V7.6a5.2 5.2 0 0 1 5.2-5.2Zm0 2a3.2 3.2 0 0 0-3.2 3.2v8.8a3.2 3.2 0 0 0 3.2 3.2h8.8a3.2 3.2 0 0 0 3.2-3.2V7.6a3.2 3.2 0 0 0-3.2-3.2Zm4.4 3a5.6 5.6 0 1 1 0 11.2 5.6 5.6 0 0 1 0-11.2Zm0 2a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2Zm5.9-2.7a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6Z"/>',
		filled: true
	},
	"brand-youtube": {
		body: '<path d="M21.6 7.2a2.8 2.8 0 0 0-2-2C17.9 4.7 12 4.7 12 4.7s-5.9 0-7.6.5a2.8 2.8 0 0 0-2 2A29 29 0 0 0 2 12a29 29 0 0 0 .4 4.8 2.8 2.8 0 0 0 2 2c1.7.5 7.6.5 7.6.5s5.9 0 7.6-.5a2.8 2.8 0 0 0 2-2A29 29 0 0 0 22 12a29 29 0 0 0-.4-4.8ZM10.1 15.1V8.9L15.3 12Z"/>',
		filled: true
	},
	"brand-tiktok": {
		body: '<path d="M16.7 2.4h-3.2v13.1a2.7 2.7 0 1 1-2.7-2.7c.3 0 .5 0 .8.1V9.6a6 6 0 0 0-.8-.1 6 6 0 1 0 6 6V8.9a7 7 0 0 0 4.1 1.3V7a4.2 4.2 0 0 1-4.2-4.2Z"/>',
		filled: true
	},
	"brand-facebook": {
		body: '<path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.7l-.4 2.9h-2.3v7A10 10 0 0 0 22 12Z"/>',
		filled: true
	},
	"brand-threads": {
		body: '<path d="M17.1 11.2a6.4 6.4 0 0 0-.3-.2c-.2-3-1.8-4.7-4.6-4.7-1.7 0-3.1.7-4 2l1.6 1.1c.6-.9 1.5-1.1 2.4-1.1 1.5 0 2.4.8 2.6 2.3a9.9 9.9 0 0 0-2.5-.2c-2.6.1-4.2 1.6-4.1 3.6.1 1 .6 1.9 1.4 2.5.7.5 1.7.8 2.7.7 1.4-.1 2.4-.6 3.1-1.6.5-.7.8-1.7 1-2.9.7.4 1.2 1 1.5 1.7.4 1.1.4 2.9-1 4.3-1.3 1.3-2.8 1.8-5.2 1.8-2.6 0-4.6-.9-5.9-2.5C5.6 16.5 5 14.6 5 12s.6-4.5 1.8-6c1.3-1.6 3.3-2.5 5.9-2.5 2.6 0 4.6.9 5.9 2.5.6.8 1.1 1.8 1.4 3l1.9-.5c-.4-1.5-1-2.7-1.8-3.8C18.4 2.7 15.9 1.6 12.7 1.6h-.1C9.5 1.6 7 2.7 5.4 4.8 3.9 6.6 3.1 9.1 3 12c.1 2.9.9 5.4 2.4 7.2 1.6 2.1 4.1 3.2 7.2 3.2h.1c2.8 0 4.8-.7 6.4-2.4 2.2-2.2 2.1-4.9 1.4-6.6-.5-1.1-1.3-2-2.4-2.6Zm-4.8 4.1c-1.1.1-2.3-.4-2.4-1.5-.1-.8.6-1.7 2.4-1.8h.5c.7 0 1.3.1 1.9.2-.2 2.6-1.4 3.1-2.4 3.1Z"/>',
		filled: true
	},
	"brand-reddit": {
		body: '<path d="M22 11.8a2.3 2.3 0 0 0-3.9-1.6 11.3 11.3 0 0 0-5.6-1.7l1-4.4 3.1.7a1.7 1.7 0 1 0 .2-1.6l-3.9-.9a.7.7 0 0 0-.8.5l-1.2 5.3a11.3 11.3 0 0 0-5.9 1.7 2.3 2.3 0 1 0-2.5 3.8 4.4 4.4 0 0 0 0 .7c0 3.5 4.1 6.4 9.2 6.4s9.2-2.9 9.2-6.4a4.4 4.4 0 0 0 0-.7 2.3 2.3 0 0 0 1.1-1.8ZM8 13.6a1.6 1.6 0 1 1 1.6 1.6A1.6 1.6 0 0 1 8 13.6Zm8.9 4.2a5.9 5.9 0 0 1-3.9 1.2h-.1a5.9 5.9 0 0 1-3.9-1.2.6.6 0 0 1 .8-.9 4.8 4.8 0 0 0 3.1.9h.1a4.8 4.8 0 0 0 3.1-.9.6.6 0 1 1 .8.9Zm-.5-2.6a1.6 1.6 0 1 1 1.6-1.6 1.6 1.6 0 0 1-1.6 1.6Z"/>',
		filled: true
	},
	"brand-linkedin": {
		body: '<path d="M19.6 2.4H4.4A2 2 0 0 0 2.4 4.4v15.2a2 2 0 0 0 2 2h15.2a2 2 0 0 0 2-2V4.4a2 2 0 0 0-2-2ZM8.2 18.4H5.5V9.8h2.7ZM6.9 8.6A1.6 1.6 0 1 1 8.5 7a1.6 1.6 0 0 1-1.6 1.6Zm11.6 9.8h-2.7v-4.2c0-1-.1-2.3-1.4-2.3s-1.6 1.1-1.6 2.2v4.3H10V9.8h2.6V11a2.9 2.9 0 0 1 2.6-1.4c2.8 0 3.3 1.8 3.3 4.2Z"/>',
		filled: true
	},
	"brand-pinterest": {
		body: '<path d="M12 2.4a9.6 9.6 0 0 0-3.5 18.5 9.2 9.2 0 0 1 .1-2.8l1.1-4.7a3.4 3.4 0 0 1-.3-1.4c0-1.3.8-2.3 1.7-2.3s1.2.6 1.2 1.3-.5 2-.8 3.1a1.4 1.4 0 0 0 1.4 1.7c1.7 0 2.9-2.2 2.9-4.8a4 4 0 0 0-4.3-4.2A4.7 4.7 0 0 0 6.7 11a2.9 2.9 0 0 0 .6 1.8.5.5 0 0 1 .2.5l-.2.8c0 .3-.2.4-.5.2-1.2-.6-2-2.4-2-3.9 0-3.2 2.3-6.1 6.7-6.1a5.9 5.9 0 0 1 6.2 5.8c0 3.5-2.2 6.3-5.2 6.3a2.7 2.7 0 0 1-2.3-1.2l-.6 2.4a11.1 11.1 0 0 1-1.3 2.7A9.6 9.6 0 1 0 12 2.4Z"/>',
		filled: true
	},
	"brand-snapchat": {
		body: '<path d="M12 2.4c2.7 0 4.7 2 4.8 4.7v2.2c.3.1.7.1 1 0 .6-.2 1.2.1 1.4.6s0 1.1-.6 1.4a6.3 6.3 0 0 1-1.5.5c.2.6.6 1.4 1.2 2.1a6 6 0 0 0 2.3 1.6c.5.2.6.7.4 1.1s-1.1.7-2 .9c-.2 0-.3.2-.4.5s-.2.7-.4.8a4 4 0 0 1-1.4 0 3.5 3.5 0 0 0-2.6.6A4.3 4.3 0 0 1 12 21a4.3 4.3 0 0 1-2.2-.6 3.5 3.5 0 0 0-2.6-.6 4 4 0 0 1-1.4 0c-.2-.1-.3-.5-.4-.8s-.2-.5-.4-.5c-.9-.2-1.8-.5-2-.9s-.1-.9.4-1.1a6 6 0 0 0 2.3-1.6c.6-.7 1-1.5 1.2-2.1a6.3 6.3 0 0 1-1.5-.5c-.6-.3-.8-.9-.6-1.4s.8-.8 1.4-.6c.3.1.7.1 1 0V7.1C7.3 4.4 9.3 2.4 12 2.4Z"/>',
		filled: true
	},
	"brand-whatsapp": {
		body: '<path d="M12.1 2.4A9.5 9.5 0 0 0 3.9 16.7L2.4 21.6l5-1.5a9.5 9.5 0 1 0 4.7-17.7Zm0 17.4a7.9 7.9 0 0 1-4-1.1l-.3-.2-3 .9.9-2.9-.2-.3a7.9 7.9 0 1 1 6.6 3.6Zm4.4-5.9c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.6.1-.6.8-.8.9-.3.2-.6 0a6.5 6.5 0 0 1-1.9-1.2 7.2 7.2 0 0 1-1.3-1.7c-.1-.2 0-.4.1-.5l.4-.5a1.8 1.8 0 0 0 .3-.4.5.5 0 0 0 0-.5c0-.1-.6-1.4-.8-1.9s-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 2.9 2.9 0 0 0-.9 2.2 5.1 5.1 0 0 0 1.1 2.7 11.6 11.6 0 0 0 4.4 3.9 8.2 8.2 0 0 0 2.6.7 2.5 2.5 0 0 0 1.7-.7 2.1 2.1 0 0 0 .5-1.5c0-.2-.2-.3-.4-.4Z"/>',
		filled: true
	},
	"brand-telegram": {
		body: '<path d="M21.7 4.1 2.9 11.4c-.9.4-.9.9-.2 1.1l4.8 1.5 1.8 5.6c.2.6.4.8 1 .8s.7-.2 1-.5l2.4-2.3 5 3.7c.9.5 1.5.2 1.8-.9l3.2-15c.3-1.3-.5-1.9-1.4-1.5ZM7.4 13.4l10.8-6.8c.5-.3.9-.1.6.2l-9.2 8.3-.4 3.8Z"/>',
		filled: true
	},
	"brand-discord": {
		body: '<path d="M19.6 5.6a16.3 16.3 0 0 0-4-1.2l-.3.5a12.4 12.4 0 0 0-3.3-.4 12.4 12.4 0 0 0-3.3.4l-.3-.5a16.3 16.3 0 0 0-4 1.2C1.9 9.5 1.2 13.3 1.6 17a16.4 16.4 0 0 0 5 2.5l1-1.7a10.6 10.6 0 0 1-1.6-.8l.4-.3a11.7 11.7 0 0 0 10 0l.4.3a10.6 10.6 0 0 1-1.6.8l1 1.7a16.4 16.4 0 0 0 5-2.5c.5-4.3-.6-8.1-3.6-11.4ZM8.5 14.7a1.9 1.9 0 0 1-1.8-2 1.8 1.8 0 0 1 1.8-2 1.8 1.8 0 0 1 1.8 2 1.9 1.9 0 0 1-1.8 2Zm7 0a1.9 1.9 0 0 1-1.8-2 1.8 1.8 0 0 1 1.8-2 1.8 1.8 0 0 1 1.8 2 1.9 1.9 0 0 1-1.8 2Z"/>',
		filled: true
	},
	"brand-bluesky": {
		body: '<path d="M6 3.8C8.6 5.7 11.4 9.6 12 11.7c.6-2.1 3.4-6 6-7.9 1.9-1.4 5-2.5 5 1.1 0 .7-.4 5.9-.7 6.8-.8 2.9-3.8 3.7-6.4 3.2 4.6.8 5.8 3.4 3.2 6-4.8 4.9-6.9-1.2-7.5-2.8l-.1-.3-.1.3c-.6 1.6-2.7 7.7-7.5 2.8-2.6-2.6-1.4-5.2 3.2-6-2.6.5-5.6-.3-6.4-3.2-.3-.9-.7-6.1-.7-6.8 0-3.6 3.1-2.5 5-1.1Z"/>',
		filled: true
	},
	/**
	 * ⚠ X is the one platform whose mark was never an emoji, so round 145's glyph census could not see
	 * it: the Content page's signal table drew the LETTER `𝕏` (U+1D54F, a mathematical double-struck
	 * capital), which is not `Extended_Pictographic` and therefore invisible to the gate. It is the
	 * same defect as every other entry in that table - a picture that renders in whatever font the OS
	 * chooses, at a weight nothing here can set - so it belongs in the set for the same reason.
	 */
	"brand-x": {
		body: '<path d="M17.2 2.8h3.3l-7.2 8.2 8.5 11.2h-6.7l-5.2-6.8-6 6.8H.6l7.7-8.8L0 2.8h6.8l4.7 6.3Zm-1.2 17.4h1.9L6.1 4.7H4.1Z"/>',
		filled: true
	},
	// The sign-in button's mark (2026-08-09). ⚠ Official geometry copied from `simple-icons`, for the
	// reason `brand-spotify` above states at length: a trademark drawn by hand is wrong on its own terms,
	// and this one is the mark on a button people are asked to trust with their account. Inlined as a
	// literal rather than imported, like Spotify's - an import here would be the crack in this file's
	// standing rule that its bodies never become reachable from a published-page renderer.
	"brand-google": {
		body: '<path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>',
		filled: true
	}
} as const;

/**
 * ⚠ The table is declared `as const` and only then widened to `IconDef`, and the order matters: a
 * bare `as const satisfies Record<string, IconDef>` narrows every entry to its own literal type, so
 * `icon.filled` stops existing on the ~90 entries that do not set it and every read of it fails to
 * compile. `as const` first, then the annotation here for the uniform VALUE type.
 *
 * ⚠ This annotation is also half the name/drawing contract: `Record<IconNameT, IconDef>` fails to
 * compile the moment `shared/iconNames.ts` names something nothing here draws. The OTHER half - a
 * drawing nobody can ask for - is not a type error (excess properties are only checked on a fresh
 * literal), so `tests/icons.test.ts` asserts it.
 */
export const ICONS: Record<IconNameT, IconDef> = RAW;

/**
 * The emoji this app used to draw, and the icon each became.
 *
 * ⚠ Several glyphs answer to more than one meaning and only ONE can be listed here, so this map is
 * the DEFAULT reading rather than the truth at every site: `▶️` is `play` almost everywhere and
 * `brand-youtube` in the platform tables, `👥` is `users` on a follower chip and `brand-facebook` in
 * the same tables, `💬` is `comment` except where it names WhatsApp. That ambiguity is itself an
 * argument for the conversion - an emoji cannot say which of the two it meant, and a name can.
 */
export const GLYPH_ICONS: Record<string, IconNameT> = {
	"⚠": "warning",
	ℹ: "info",
	"✅": "check-circle",
	"🚫": "ban",
	"🚩": "flag",
	"🛡": "shield",
	"🎉": "party",
	"🏆": "trophy",
	"❤": "heart",
	// ⚠ Two codepoints, one picture: `❤` is the emoji heart and `♥` is the CARD SUIT, which several
	// call sites used interchangeably because most fonts draw them alike. Both map to the same icon -
	// which is exactly the ambiguity the conversion removes.
	"♥": "heart",
	"👍": "thumbs-up",
	"✋": "hand",
	"↗": "arrow-up-right",
	"⬇": "arrow-down",
	"⬆": "arrow-up",
	"↩": "arrow-back",
	"🔗": "link",
	"🧭": "compass",
	"🔍": "search",
	"🔎": "search",
	"🌐": "globe",
	"🌍": "globe",
	"🚀": "rocket",
	"🔌": "plug",
	"⚙": "gear",
	"▶": "play",
	"⏸": "pause",
	"⏹": "stop",
	"🔁": "repeat",
	"🔀": "shuffle",
	"🎤": "mic",
	"🎙": "mic",
	"🎧": "headphones",
	"🎵": "music-note",
	"🎶": "music-notes",
	"💿": "disc",
	"🎞": "film",
	"🎬": "clapper",
	"🎥": "video-camera",
	"📷": "camera",
	"📸": "brand-instagram",
	"👁": "eye",
	"🌊": "waves",
	"〰": "wave-line",
	"🫁": "lungs",
	"🌫": "fog",
	"🌅": "sunrise",
	"🌉": "bridge",
	"🎯": "target",
	"🎛": "sliders",
	"✨": "sparkles",
	"🎨": "palette",
	"📐": "angle",
	"✏": "pencil",
	"✍": "write",
	"🔤": "type",
	"🧪": "flask",
	"⚡": "bolt",
	"💡": "bulb",
	"🕐": "clock",
	"🕒": "clock",
	"🕓": "clock",
	"🕘": "clock",
	"⏱": "stopwatch",
	"⏳": "hourglass",
	"📄": "file",
	"📁": "folder",
	"📂": "folder",
	"📝": "note",
	"📒": "journal",
	"📖": "book",
	"📋": "clipboard",
	"🗄": "archive",
	"🔖": "bookmark",
	"🏷": "tag",
	"📎": "paperclip",
	"📊": "chart-bar",
	"📈": "chart-line",
	"✉": "mail",
	"📬": "mailbox",
	"📮": "postbox",
	"📤": "send",
	"📥": "inbox",
	"💬": "comment",
	"📣": "megaphone",
	"📢": "megaphone",
	// ⚠ There is no `handshake` drawing, on purpose - see the note in `shared/iconNames.ts`. `users`
	// is what the one call site that had this glyph means: two parties in a relationship.
	"🤝": "users",
	"👥": "users",
	"👤": "user",
	"💰": "money",
	"💸": "money",
	"🎁": "gift",
	"♻": "recycle",
	"📱": "phone",
	"🤖": "robot",
	"💾": "save",
	"🧵": "brand-threads",
	"👽": "brand-reddit",
	"💼": "brand-linkedin",
	"📌": "brand-pinterest",
	"👻": "brand-snapchat",
	"✈": "brand-telegram",
	"🎮": "brand-discord",
	"🦋": "brand-bluesky"
};
